import { useRouter } from 'next/navigation';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { Divider } from '@/app_components/SignIn/Components/SignInSharedComponents';
import { SocialSignInContainer } from '@/app_components/SignIn/SSO/SocialSignInContainer';
import { SocialSignInController } from '@/app_components/SignIn/SSO/SocialSignInController';
import { useUser } from '@/app_lib/providers/UserProvider';
import { sendRegistrationConversionEvent } from '@/app_lib/services/Piano/pianoHelpers';
import type { SignInFlow } from '@/app_lib/types/accounts/signInFlow.entities';
import type { SocialLoginTypeError, SocialUrls } from '@/app_lib/types/accounts/signin';
import { redirectOnAuth } from '@/app_lib/utils/signInFlowHelpers';

interface SocialSignInProps {
  flow: SignInFlow;
  redirectCookieKey: string;
  socialLoginLoading: boolean;
  setSocialLoginLoading: Dispatch<SetStateAction<boolean>>;
  socialUrls: SocialUrls;
  disableSocialLogins: boolean;
}

export const SocialSignIn = ({
  disableSocialLogins,
  flow,
  redirectCookieKey,
  socialLoginLoading,
  setSocialLoginLoading,
  socialUrls,
}: SocialSignInProps) => {
  const [socialLoginTypeError, setSocialLoginTypeError] = useState<SocialLoginTypeError | null>(null);
  const router = useRouter();
  const { refreshUserAccess } = useUser();

  const handleSocialLoginSuccess = useCallback(
    async (isRegistration: boolean) => {
      if (isRegistration) {
        sendRegistrationConversionEvent();
      }
      await redirectOnAuth({ flow, redirectCookieKey, refreshUserAccess, router });
    },
    [redirectCookieKey, flow, refreshUserAccess, router],
  );

  useEffect(() => {
    if (!socialLoginTypeError) {
      return;
    }
    const dismiss = () => setSocialLoginTypeError(null);
    window.addEventListener('click', dismiss);
    return () => window.removeEventListener('click', dismiss);
  }, [socialLoginTypeError]);

  return (
    <>
      <Divider />
      <div className='w-full max-w-[340px]'>
        <SocialSignInController
          flow={flow}
          onLoadingChange={setSocialLoginLoading}
          onLoginError={setSocialLoginTypeError}
          onLoginSuccess={handleSocialLoginSuccess}
        >
          <SocialSignInContainer
            disableSocialLogins={disableSocialLogins}
            onLoadingChange={setSocialLoginLoading}
            socialLoginLoading={socialLoginLoading}
            socialLoginTypeError={socialLoginTypeError}
            socialUrls={socialUrls}
          />
        </SocialSignInController>
      </div>
    </>
  );
};
